import React, { useEffect, useState, useRef } from 'react';
import { FormikProps } from 'formik';
import { toString, toNumber, isNil } from 'lodash';
import { Button, Modal, Tabs } from 'antd';

import { TabValidationStatuses } from 'enums';
import useTabs from 'hooks/useTabs';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { updateClientAdditionalInfo } from 'redux/reducers/clientSlice';
import {
  AssignmentStatusTypeEnum,
  ResourceDownloadResponse,
} from 'api/backofficeApiClient';
import {
  CustomerAdditionalInfoResponseModel,
  EditCustomerAdditionalInfoRequestModel,
  RateChangeModeEnum,
} from 'api/adminApiClient';
import RateChangeConfirmationModal from './GeneralContractInfo/RateChangeConfirmationModal';
import { getAssignments } from 'redux/reducers/assignmentSlice';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';
import { getTabValidationIcon } from 'utils/tabUtils';
import GeneralContractInfo, {
  GeneralContractInfoFormValues,
} from './GeneralContractInfo';
import FinancialInfo, { FinancialInfoFormValues } from './FinancialInfo';
import AssignmentRequirements, {
  AssignmentRequirementsFormValues,
} from './AssignmentRequirements';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  getAllCities,
  getAllCountries,
  getAllCountryCities,
  getAllStates,
} from 'redux/reducers/regionSlice';

export interface EditClientAdditionalInfoInitialValues {
  generalContractInfo: GeneralContractInfoFormValues;
  financialInfo?: FinancialInfoFormValues;
  assignmentRequirements?: AssignmentRequirementsFormValues;
}

type Props = {
  visible: boolean;
  customerId: string;
  customerAdditionalInfo?: CustomerAdditionalInfoResponseModel;
  initialValues?: EditClientAdditionalInfoInitialValues;
  certificateFileInfo?: ResourceDownloadResponse;
  purchaseOrderFile?: ResourceDownloadResponse;
  sowTemplateFileInfo?: ResourceDownloadResponse;
  onboardingDocumentationFileInfo?: ResourceDownloadResponse[];
  isDeclined?: boolean;
  onClose: () => void;
  onSubmit?: () => void;
};

enum TabKeys {
  GeneralContactInfo = 0,
  FinancialInfo = 1,
  AssignmentRequirements = 2,
}

const getInitialTabs = () => [
  {
    title: 'Contract Information',
    status: TabValidationStatuses.Success,
  },
  {
    title: 'Financial Information',
    status: TabValidationStatuses.Success,
  },
  {
    title: 'Assignment Information',
    status: TabValidationStatuses.Success,
  },
];

type GeneralContractInfoFormikProps =
  FormikProps<GeneralContractInfoFormValues>;
type FinancialInfoFormikProps = FormikProps<FinancialInfoFormValues>;
type AssignmentRequirementsFormikProps =
  FormikProps<AssignmentRequirementsFormValues>;

export default function EditClientAdditionalInfoModal(props: Props) {
  const {
    visible,
    customerId,
    customerAdditionalInfo,
    initialValues,
    certificateFileInfo,
    purchaseOrderFile,
    onboardingDocumentationFileInfo,
    sowTemplateFileInfo,
    isDeclined,
    onClose,
    onSubmit,
  } = props;
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState(TabKeys.GeneralContactInfo);
  const [tabs, onFormChange] = useTabs(getInitialTabs());
  const [isLoading, setIsLoading] = useState(false);
  const submitData = useRef<EditCustomerAdditionalInfoRequestModel>();
  const rateChangeMode = useRef<RateChangeModeEnum>(
    RateChangeModeEnum.OnlyNewAssignments,
  );
  const [showRateChangeModal, setShowRateChangeModal] = useState(false);
  const isFirstEdit = !customerAdditionalInfo?.financialInformation;

  const generalContractInfoFormRef: any = useRef();
  const financialInfoFormRef: any = useRef();
  const assignmentRequirementsFormRef: any = useRef();

  useEffect(() => {
    dispatch(getAllStates());
    dispatch(getAllCities());
    dispatch(getAllCountries());
    dispatch(getAllCountryCities());
  }, [dispatch]);

  const handleTabChange = (activeTab: string) =>
    setActiveTab(toNumber(activeTab));

  const handleNextStep = () => setActiveTab((step) => step + 1);
  const handlePrevStep = () => setActiveTab((step) => step - 1);

  const isFormValid = () => {
    return [
      generalContractInfoFormRef.current?.isValid,
      financialInfoFormRef.current?.isValid,
      assignmentRequirementsFormRef.current?.isValid,
    ].every(Boolean);
  };

  const isFormSubmitting = () => {
    return [
      generalContractInfoFormRef.current?.isSubmitting,
      financialInfoFormRef.current?.isSubmitting,
      assignmentRequirementsFormRef.current?.isSubmitting,
    ].every(Boolean);
  };

  const isFormDirty = () => {
    return [
      generalContractInfoFormRef.current?.dirty,
      financialInfoFormRef.current?.dirty,
      assignmentRequirementsFormRef.current?.dirty,
    ].some(Boolean);
  };

  const resetFormSubmitting = () => {
    [
      generalContractInfoFormRef.current,
      financialInfoFormRef.current,
      assignmentRequirementsFormRef.current,
    ].forEach((form) => form.setSubmitting(false));
  };

  const handleModalCancel = () => {
    if (isFormSubmitting()) return;

    if (isFormDirty()) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  const handleSubmit = async () => {
    const generalContractInfoForm: GeneralContractInfoFormikProps =
      generalContractInfoFormRef.current;
    const financialInfoForm: FinancialInfoFormikProps =
      financialInfoFormRef.current;
    const assignmentRequirementsForm: AssignmentRequirementsFormikProps =
      assignmentRequirementsFormRef.current;

    await Promise.all([
      generalContractInfoForm.submitForm(),
      financialInfoForm.submitForm(),
      assignmentRequirementsForm.submitForm(),
    ]);

    if (!isFormValid()) return;

    const gci = generalContractInfoForm.values;
    const {
      requiresCustomCOI,
      submittedTo,
      certificate,
      contractSpecialInstructions,
      sowTemplate,
      sowRequired,
      sowSpecialInstructions,
      canContractBeAssigned,
      useOfNameLogoPermitted,
      customerAgreementType,
      governingLawJurisdiction,
      indemnificationType,
      limitationOfLiability,
      rightToInspectOrAudit,
      contractedMBPServiceLines,
      dataPrivacyRequirements,
      dataPrivacyInstructions,
      contractRenewalTerm,
      contractEffectiveDate,
      contractExpirationDate,
      noticePeriodInDays,
      attentionParty,
      noticePartyCountryId,
      noticePartyStateId,
      noticePartyCity,
      noticePartyInternationalCityName,
      noticePartyInternationalStateName,
      noticePartyAddress,
      noticePartyZipCode,
      noticePartyEmail,
      noticePartyPhone,
      noticePartyFaxNumber,
      noticeMethod,
      declinationReason,
    } = gci;

    const {
      creditCheck,
      commissionType,
      notes,
      paymentTerms,
      markupRate,
      otRateMultiplier,
      dtBillRateMultiplier,
      MSPFee,
      VMSFee,
      invoicingInfo,
      invoicingInstructions,
      invoicingCycle,
      payrollCycle,
      url,
      providerName,
      isThirdWorkforce,
      purchaseOrder,
      purchaseOrderInstructions,
      purchaseOrderFileKey,
      customerCostingMethodType,
      contractorConversionFees,
      customerCreditNotes,
      dunsNumber,
      invoiceCutOffPeriod,
      invoiceDisputePeriod,
      paymentMethod,
      payorName,
    } = financialInfoForm.values;

    const {
      contractorsPermitted,
      preScreeningRequired,
      preScreeningBilledTo,
      preScreening,
      designatedPreScreening,
      isOnboardingDocumentationRequired,
      specialInstructions,
      onboardingDocumentation,
      workPerformance,
      timeEntry,
      assignmentNotes,
      breakInService,
      isDrugScreensRequired,
      drugScreenRequirements,
      isCredentialingRequired,
      credentialingRequirements,
      isSpecialRatesDefined,
      specialRateRequirements,
      cspNotes,
    } = assignmentRequirementsForm.values;

    const noticeUseStates = checkIsCountriesWithStatesDropdown(
      noticePartyCountryId,
    );

    const data: EditCustomerAdditionalInfoRequestModel = {
      contractInformation: {
        insuranceRequirements: {
          requiresCustomCOI: !isNil(requiresCustomCOI)
            ? requiresCustomCOI
            : undefined,
          submittedTo: submittedTo.trim() || undefined,
          certificateOfInsuranceCreatedFileKey: certificate?.length
            ? certificate[0]
            : undefined,
        },
        contractRequirements: {
          canContractBeAssigned: !isNil(canContractBeAssigned)
            ? canContractBeAssigned
            : undefined,
          useOfNameLogoPermitted: !isNil(useOfNameLogoPermitted)
            ? useOfNameLogoPermitted
            : undefined,
          customerAgreementType: customerAgreementType ?? undefined,
          governingLawJurisdiction:
            governingLawJurisdiction?.trim() || undefined,
          indemnificationType: indemnificationType ?? undefined,
          limitationOfLiability: limitationOfLiability?.trim() || undefined,
          rightToInspectOrAudit: !isNil(rightToInspectOrAudit)
            ? rightToInspectOrAudit
            : undefined,
          contractedMBPServiceLines:
            contractedMBPServiceLines?.length &&
            contractedMBPServiceLines.length > 0
              ? contractedMBPServiceLines
              : undefined,
          dataPrivacyRequirements: !isNil(dataPrivacyRequirements)
            ? dataPrivacyRequirements
            : undefined,
          dataPrivacyInstructions:
            dataPrivacyInstructions?.trim() || undefined,
          contractRenewalTerm: contractRenewalTerm ?? undefined,
          contractEffectiveDate: contractEffectiveDate
            ? new Date(contractEffectiveDate)
            : undefined,
          contractExpirationDate: contractExpirationDate
            ? new Date(contractExpirationDate)
            : undefined,
          noticePeriodInDays:
            typeof noticePeriodInDays === 'number'
              ? noticePeriodInDays
              : undefined,
          sowRequired: sowRequired,
          sowTemplateFileKey: sowTemplate?.length ? sowTemplate[0] : undefined,
          sowSpecialInstructions: sowSpecialInstructions.trim() || undefined,
          declinationReason: declinationReason?.trim() || undefined,
          contractSpecialInstructions:
            contractSpecialInstructions.trim() || undefined,
        },
        noticeParty: {
          attentionParty: attentionParty?.trim() || undefined,
          location: {
            countryId: noticePartyCountryId,
            address: noticePartyAddress?.trim() || undefined,
            zipCode: noticePartyZipCode?.trim() || undefined,
            ...(noticeUseStates
              ? {
                  stateId: noticePartyStateId,
                  city: noticePartyCity?.trim() || undefined,
                }
              : {
                  internationalCityName:
                    noticePartyInternationalCityName?.trim() || undefined,
                  internationalStateName:
                    noticePartyInternationalStateName?.trim() || undefined,
                }),
          },
          email: noticePartyEmail?.trim() || undefined,
          phone: noticePartyPhone?.trim() || undefined,
          faxNumber: noticePartyFaxNumber?.trim() || undefined,
          noticeMethod: noticeMethod?.trim() || undefined,
        },
      },
      financialInformation: {
        financialOverview: {
          creditCheckScore: creditCheck.trim() || undefined,
          fundingType: commissionType || undefined,
          financialSpecialInstructions: notes.trim() || undefined,
          paymentTerm: paymentTerms || undefined,
          customerCostingMethodType: customerCostingMethodType ?? undefined,
          specialInvoicingInstructions:
            invoicingInstructions.trim() || undefined,
          invoicingInformation: invoicingInfo.trim() || undefined,
          invoicingCycle: invoicingCycle || undefined,
          payrollCycle: payrollCycle || undefined,
          purchaseOrder: purchaseOrder,
          purchaseOrderFileKey: purchaseOrderFileKey?.length
            ? purchaseOrderFileKey[0]
            : undefined,
          purchaseOrderInstructions: purchaseOrderInstructions || undefined,
          contractorConversionFees: !isNil(contractorConversionFees)
            ? contractorConversionFees
            : undefined,
          customerCreditNotes: customerCreditNotes?.trim() || undefined,
          dunsNumber: dunsNumber?.trim() || undefined,
          invoiceCutOffPeriod: invoiceCutOffPeriod?.trim() || undefined,
          invoiceDisputePeriod: invoiceDisputePeriod?.trim() || undefined,
          paymentMethod: paymentMethod ?? undefined,
          payorName: payorName?.trim() || undefined,
        },
        rateOverview: {
          fixedMarkupRate: markupRate.trim() || undefined,
          otRateMultiplier: otRateMultiplier.trim() || undefined,
          dtBillRateMultiplier: !isNil(dtBillRateMultiplier)
            ? dtBillRateMultiplier
            : undefined,
        },
        mspOrVmsOverview: {
          mspFee: typeof MSPFee === 'number' ? MSPFee : undefined,
          vmsFee: typeof VMSFee === 'number' ? VMSFee : undefined,
          isThereMspOrVms: !isNil(isThirdWorkforce)
            ? isThirdWorkforce
            : undefined,
          url: url || undefined,
          mspOrVmsProviderName: providerName || undefined,
        },
      },
      assignmentInformation: {
        assignmentRequirements: {
          isIndependentContractorsOrSubcontractors: contractorsPermitted,
          isPreScreeningRequired: preScreeningRequired,
          preScreeningBilledTo,
          contractBackgroundScreenRequirements:
            preScreening.trim() || undefined,
          isOnboardingDocumentationRequired,
          designatedPreScreeningProvider: designatedPreScreening,
          onboardingDocumentationFileKeys:
            onboardingDocumentation.length && isOnboardingDocumentationRequired
              ? onboardingDocumentation
              : undefined,
          specialInstructions: specialInstructions.trim() || undefined,
          isDrugScreensRequired: !isNil(isDrugScreensRequired)
            ? isDrugScreensRequired
            : undefined,
          drugScreenRequirements: drugScreenRequirements?.trim() || undefined,
          isCredentialingRequired: !isNil(isCredentialingRequired)
            ? isCredentialingRequired
            : undefined,
          credentialingRequirements:
            credentialingRequirements?.trim() || undefined,
          isSpecialRatesDefined: !isNil(isSpecialRatesDefined)
            ? isSpecialRatesDefined
            : undefined,
          specialRateRequirements:
            specialRateRequirements?.trim() || undefined,
          cspNotes: cspNotes?.trim() || undefined,
        },
        specialAssignmentRequirements: {
          guaranteedWorkPerformance: workPerformance.trim() || undefined,
          timecardSpecialInstructions: assignmentNotes.trim() || undefined,
          timeEntry: timeEntry.trim() || undefined,
          breakInService: !isNil(breakInService) ? breakInService : undefined,
        },
      },
    };

    submitData.current = data;
    const hasRateChanges =
      initialValues?.financialInfo?.MSPFee !==
        data.financialInformation?.mspOrVmsOverview?.mspFee ||
      initialValues?.financialInfo?.VMSFee !==
        data.financialInformation?.mspOrVmsOverview?.vmsFee;

    if (!isFirstEdit) {
      if (hasRateChanges) {
        // Per MDB-186, "Apply to all active and pending assignments" cascades to all
        // in-progress assignments — both Active and Request (pending) — so the
        // prompt must also be shown when only pending assignments exist.
        const result = await dispatch(
          getAssignments({
            requestId: Date.now(),
            pageNumber: 0,
            isAdminRole: true,
            customerIds: [customerId],
            statuses: [
              AssignmentStatusTypeEnum.Active,
              AssignmentStatusTypeEnum.Request,
            ],
          }),
        );
        if (getAssignments.fulfilled.match(result)) {
          const activeOrPendingCount = result.payload.totalCount ?? 0;
          if (activeOrPendingCount > 0) {
            setShowRateChangeModal(true);
            return;
          }
        }
      } else {
        rateChangeMode.current = RateChangeModeEnum.None;
        submitAdditionalInfo();
        return;
      }
    }

    rateChangeMode.current = RateChangeModeEnum.OnlyNewAssignments;
    submitAdditionalInfo();
  };

  const handleRateChangeModalCancel = () => {
    setShowRateChangeModal(false);
    const financialInfoForm: FinancialInfoFormikProps =
      financialInfoFormRef.current;
    if (financialInfoForm) {
      financialInfoForm.setFieldValue(
        'MSPFee',
        typeof initialValues?.financialInfo?.MSPFee === 'number'
          ? initialValues.financialInfo.MSPFee
          : undefined,
      );
      financialInfoForm.setFieldValue(
        'VMSFee',
        typeof initialValues?.financialInfo?.VMSFee === 'number'
          ? initialValues.financialInfo.VMSFee
          : undefined,
      );
    }

    resetFormSubmitting();
  };

  const handleApplyToAllActiveAssignments = () => {
    rateChangeMode.current = RateChangeModeEnum.NewAndActiveAssignments;
    setShowRateChangeModal(false);
    submitAdditionalInfo();
  };

  const handleApplyOnlyToNewAssignments = () => {
    rateChangeMode.current = RateChangeModeEnum.OnlyNewAssignments;
    setShowRateChangeModal(false);
    submitAdditionalInfo();
  };

  const submitAdditionalInfo = async () => {
    setIsLoading(true);
    const result = await dispatch(
      updateClientAdditionalInfo({
        customerId,
        data: {
          ...submitData.current!,
          rateChangeMode: rateChangeMode.current,
        },
      }),
    );
    setIsLoading(false);

    if (updateClientAdditionalInfo.fulfilled.match(result)) {
      BaseAlert.success('Customer has been updated.');
      onClose();

      onSubmit?.();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);

      resetFormSubmitting();
    }
  };

  return (
    <>
      <RateChangeConfirmationModal
        open={showRateChangeModal}
        onCancel={handleRateChangeModalCancel}
        onApplyToAllActiveAssignments={handleApplyToAllActiveAssignments}
        onApplyOnlyToNewAssignments={handleApplyOnlyToNewAssignments}
      />
    <BaseModal
      title="Edit customer additional information"
      maxWidth={888}
      open={visible}
      onCancel={handleModalCancel}
      className="modal-with-tabs"
    >
      <Tabs
        activeKey={toString(activeTab)}
        onChange={(activeKey: string) => handleTabChange(activeKey)}
        animated={false}
        className="icon-tabs modal-scroll-wrapper"
      >
        <Tabs.TabPane
          key={toString(TabKeys.GeneralContactInfo)}
          tab={
            <div
              className="tab-pane-wrapper"
              data-id="editCustomerAdditionInfoFinancialTab"
            >
              <span className="tab-title">
                {tabs[TabKeys.GeneralContactInfo].title}
              </span>
              {getTabValidationIcon(tabs[TabKeys.GeneralContactInfo].status)}
            </div>
          }
        >
          <GeneralContractInfo
            formRef={generalContractInfoFormRef}
            certificateFileInfo={certificateFileInfo}
            initialValues={initialValues?.generalContractInfo}
            onChange={onFormChange[TabKeys.GeneralContactInfo]}
            sowTemplateFileInfo={sowTemplateFileInfo}
            isDeclined={isDeclined}
          />
        </Tabs.TabPane>

        <Tabs.TabPane
          forceRender
          key={toString(TabKeys.FinancialInfo)}
          tab={
            <div
              className="tab-pane-wrapper"
              data-id="editCustomerAdditionInfoOverviewTab"
            >
              <span className="tab-title">
                {tabs[TabKeys.FinancialInfo].title}
              </span>
              {getTabValidationIcon(tabs[TabKeys.FinancialInfo].status)}
            </div>
          }
        >
          <FinancialInfo
            formRef={financialInfoFormRef}
            purchaseOrderFileInfo={purchaseOrderFile}
            initialValues={initialValues?.financialInfo}
            onChange={onFormChange[TabKeys.FinancialInfo]}
          />
        </Tabs.TabPane>

        <Tabs.TabPane
          forceRender
          key={toString(TabKeys.AssignmentRequirements)}
          tab={
            <div
              className="tab-pane-wrapper"
              data-id="editCustomerAdditionInfoAssignmentsTab"
            >
              <span className="tab-title">
                {tabs[TabKeys.AssignmentRequirements].title}
              </span>
              {getTabValidationIcon(
                tabs[TabKeys.AssignmentRequirements].status,
              )}
            </div>
          }
        >
          <AssignmentRequirements
            formRef={assignmentRequirementsFormRef}
            onboardingDocumentationFileInfo={onboardingDocumentationFileInfo}
            initialValues={initialValues?.assignmentRequirements}
            onChange={onFormChange[TabKeys.AssignmentRequirements]}
          />
        </Tabs.TabPane>
      </Tabs>

      <BaseModalFooter className="button-space-between">
        <div className="d-flex">
          <Button
            className="with-icon modal-icon-btn"
            onClick={handleModalCancel}
          >
            <span className="icon icon-closed-outlined" />
            Cancel
          </Button>
        </div>

        <div className="d-flex">
          {activeTab !== TabKeys.GeneralContactInfo && (
            <Button
              className="with-icon modal-icon-btn"
              onClick={handlePrevStep}
            >
              <span className="icon icon-double-left" />
              Back
            </Button>
          )}

          {activeTab < tabs.length - 1 && (
            <Button
              className="with-icon"
              type="primary"
              onClick={handleNextStep}
            >
              Next
              <span className="icon icon-double-right" />
            </Button>
          )}

          {activeTab === tabs.length - 1 && (
            <Button
              type="primary"
              data-id="editCustomerAdditionInfoBtn"
              disabled={isLoading}
              loading={isLoading}
              onClick={handleSubmit}
            >
              Save
            </Button>
          )}
        </div>
      </BaseModalFooter>
    </BaseModal>
    </>
  );
}
